import React from 'react';

/**
 * The semantic-table building blocks, in one place.
 *
 * The portal has two table systems: the older div-and-percentage-width one
 * (shared/components/ui/TableHead + TableData, 42 screens) and a plain
 * <table> (88 screens, all the newer work). This is not the older one and does
 * not replace it — it is the second one, finally written down.
 *
 * Th and Td had been copy-pasted into 16 screens each. They had very nearly
 * converged on px-4 py-3 by themselves, which is the strongest argument that
 * one shared copy was always the right shape; the few py-2 stragglers were
 * drift, not intent.
 *
 *   <TableCard title="Muster">
 *     <Table>
 *       <THead><tr><Th>Workman</Th><Th align="right">Hours</Th></tr></THead>
 *       <TBody>
 *         <Tr><Td>Ram</Td><Td align="right" numeric>8</Td></Tr>
 *       </TBody>
 *     </Table>
 *   </TableCard>
 */

/** Header cell. `align="right"` for the columns that carry figures. */
export const Th = ({ children, align = 'left', className = '' }) => (
  <th
    scope="col"
    className={`whitespace-nowrap px-4 py-3 font-medium ${
      align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'
    } ${className}`}>
    {children}
  </th>
);

/**
 * Body cell. `numeric` turns on tabular figures so a column of rupees lines up
 * on the decimal instead of jittering — which the clones never did, and which
 * is the difference between a column you can scan and one you have to read.
 */
export const Td = ({ children, align = 'left', numeric = false, className = '' }) => (
  <td
    className={`px-4 py-3 align-top ${
      align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'
    } ${numeric ? 'tabular-nums' : ''} ${className}`}>
    {children}
  </td>
);

export const Table = ({ children, className = '' }) => (
  <table className={`w-full text-sm ${className}`}>{children}</table>
);

export const THead = ({ children, className = '' }) => (
  <thead className={`bg-surface-muted text-gray-600 ${className}`}>{children}</thead>
);

export const TBody = ({ children, className = '' }) => (
  <tbody className={`divide-y divide-hairline ${className}`}>{children}</tbody>
);

export const Tr = ({ children, onClick, className = '' }) => (
  <tr
    onClick={onClick}
    className={`${onClick ? 'cursor-pointer' : ''} hover:bg-surface-muted ${className}`}>
    {children}
  </tr>
);

/**
 * The shell a table sits in: hairline, rounded corners, clipped so the header
 * tint does not spill past the radius. Optional title bar, because a table
 * with no heading is the other thing the cloned screens kept getting wrong.
 */
export const TableCard = ({ title, action, children, className = '' }) => (
  <div className={`overflow-hidden rounded-panel border border-hairline bg-surface ${className}`}>
    {(title || action) && (
      <div className="flex items-baseline justify-between border-b border-hairline px-4 py-3">
        {title && <h3 className="text-sm font-medium text-gray-800">{title}</h3>}
        {action}
      </div>
    )}
    <div className="overflow-x-auto">{children}</div>
  </div>
);

/**
 * The "nothing here" row. Spans the full table so the message sits under the
 * headers rather than squashed into the first column.
 */
export const EmptyRow = ({ colSpan, children = 'Nothing to show.' }) => (
  <tr>
    <td colSpan={colSpan} className="px-4 py-12 text-center text-sm text-gray-500">
      {children}
    </td>
  </tr>
);
